import React from "react"
import { useUpload } from "@/contexts/UploadContext"
import { UploadCloud, Loader2 } from "lucide-react"
import { formatFileSize } from "@/utils/formatFileSize"

/**
 * Props for the {@link GlobalUploadIndicator} component.
 * Currently, no props are defined, but this type can be extended in the future.
 */
type GlobalUploadIndicatorProps = {
	// onClick?: () => void; // Example: To open the UploadQueueModal
}

/**
 * A global UI element that provides a summary of ongoing file uploads.
 * It indicates if there are active uploads and can serve as an entry point
 * to a more detailed upload queue view.
 *
 * @component
 * @param props - The props for the component.
 * @returns A React element representing the global upload indicator.
 */
const GlobalUploadIndicator: React.FC<GlobalUploadIndicatorProps> = (props) => {
	const { uploadState } = useUpload()
	const [recentlyCompleted, setRecentlyCompleted] = React.useState<Set<string>>(new Set())

	// Track recently completed uploads
	React.useEffect(() => {
		const completedTasks = uploadState.tasks.filter(task => task.status === "completed")
		const completedIds = new Set(completedTasks.map(task => task.id))
		
		// Add newly completed tasks to recentlyCompleted
		completedIds.forEach(id => {
			if (!recentlyCompleted.has(id)) {
				setRecentlyCompleted(prev => new Set(prev).add(id))
				// Remove after 10 minutes
				setTimeout(() => {
					setRecentlyCompleted(prev => {
						const newSet = new Set(prev)
						newSet.delete(id)
						return newSet
					})
				}, 10 * 60 * 1000) // 10 minutes
			}
		})
	}, [uploadState.tasks, recentlyCompleted])

	const activeUploads = uploadState.tasks.filter(
		(task) =>
			task.status === "uploading" ||
			task.status === "pending_resume" ||
			task.status === "pending" ||
			(task.status === "completed" && recentlyCompleted.has(task.id))
	)

	if (activeUploads.length === 0) {
		return null // Don't render anything if there are no active uploads
	}

	const isUploading = activeUploads.some((task) => task.status === "uploading")
	const totalProgress =
		activeUploads.reduce((acc, task) => acc + task.progress, 0) /
		activeUploads.length
		
	// Calculate total bytes uploaded and total file size
	const totalBytesUploaded = activeUploads.reduce(
		(acc, task) => acc + task.uploadedBytes,
		0
	)
	const totalFileSize = activeUploads.reduce(
		(acc, task) => acc + task.fileSize,
		0
	)

	return (
		<div
			className="fixed bottom-4 right-4 z-50 p-3 bg-background border border-border rounded-lg shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
			role="status"
			aria-live="polite"
			aria-label={`File uploads active: ${activeUploads.length} item(s)`}
			// onClick={props.onClick} // Example: if we add a prop to open modal
		>
			<div className="flex items-center space-x-2">
				{isUploading ? (
					<Loader2 className="h-6 w-6 text-primary animate-spin" />
				) : (
					<UploadCloud className="h-6 w-6 text-primary" />
				)}
				<div>
					<p className="text-sm font-medium text-foreground">
						{activeUploads.length} Upload{activeUploads.length > 1 ? "s" : ""}{" "}
						Active
					</p>
					{isUploading && (
						<p className="text-xs text-muted-foreground">
							{formatFileSize(totalBytesUploaded)} of {formatFileSize(totalFileSize)} ({totalProgress.toFixed(0)}%)
						</p>
					)}
					{!isUploading &&
						activeUploads.some((task) => task.status === "pending_resume") && (
							<p className="text-xs text-muted-foreground">
								Some uploads require attention.
							</p>
						)}
				</div>
			</div>
		</div>
	)
}

export default GlobalUploadIndicator
